"use client";
import classNames from "classnames";
// import { Box, Space, Stack } from "@fastlooptech/layout-components";
import { spaceUnit } from "@/helpers/utils.helper";
import { useResponsive } from "@/client/hooks/useResponsive";
import { ReactNode, useEffect, useRef, useState } from "react";
import { CarouselControls } from "@/components/Controllers/CarouselControls/CarouselControls";
import { Box, Space, Stack } from "@/layouts/LayoutComponents";

export interface ColumnTextScrollableProps {
  "data-test-id"?: string;
  /**
   * The ColumnTextScrollable className.
   * @type {string}
   */
  className?: string;
  /**
   * The left column of the ColumnTextScrollable.
   * @type ReactNode
   * @required
   */
  leftColumn: ReactNode;
  /**
   * The left column className the ColumnTextScrollable.
   * @type {string}
   */
  leftColumnClassName?: string;
  /**
   * The right column of the ColumnTextScrollable.
   * @type ReactNode
   * @required
   */
  rightColumn: ReactNode;
  /**
   * The right column className the ColumnTextScrollable.
   * @type {string}
   */
  rightColumnClassName?: string;
  /**
   * Enable animation triggered whe the element is visible.
   * @type boolean
   * @optional
   */
  animated?: boolean;
  /**
   * The animation type triggered whe the element is visible. Default is fadeIn
   * @type string
   * @optional
   */
  animation?: string;
}

export function ColumnTextScrollable(props: ColumnTextScrollableProps) {
  const { isMd, isLg } = useResponsive();
  const cardListColumnRef = useRef<HTMLDivElement>(null);
  const [cardListScrolled, setCardListScrolled] = useState(false);
  const [activeChildIndex, setActiveChildIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      for (let i = 0; i < cardListColumnRef?.current?.children?.length!; i++) {
        const child = cardListColumnRef?.current?.children?.[i] as HTMLElement;
        const { top, bottom } = child.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const childHeight = bottom - top;
        const viewportCenter = windowHeight / 2;
        const childCenter = (top + bottom) / 2;

        if (childCenter >= viewportCenter - childHeight / 2 && childCenter <= viewportCenter + childHeight / 2) {
          setActiveChildIndex(i);
          return;
        }

        if (window?.scrollY === 0) {
          setActiveChildIndex(0);
          return;
        }
      }
      setCardListScrolled(!cardListScrolled);
    };

    window?.addEventListener("scroll", handleScroll);
    return () => {
      window?.removeEventListener("scroll", handleScroll);
    };
  }, [cardListScrolled]);

  return (
    <Box
      column={!isMd}
      data-test-id={props["data-test-id"]}
      className={classNames(props.className, {
        "w-full max-w-[1440px] h-full m-auto !grid grid-cols-12": isMd,
      })}
    >
      <Box
        column
        className={classNames(props.leftColumnClassName, {
          "h-fit sticky top-18 !grid col-span-5 justify-start": isMd,
          "pt-5 px-2": !isMd,
        })}
        hAlignContent="left"
        grow
        data-aos={props.animated ? (props.animation || 'fade-in') : ''}
      >
        {props.leftColumn}
      </Box>
      <Stack
        ref={cardListColumnRef}
        gap={spaceUnit * (isMd ? 20 : 10)}
        column
        className={classNames("!grid col-span-6", props.rightColumnClassName, { "h-full pl-5": isMd })}
      >
        {props.rightColumn}
        <Space size={spaceUnit} />
      </Stack>
      {isLg && (
        <Box column className="!grid col-span-1 h-fit sticky top-[265px]" hAlignContent="center">
          <CarouselControls
            total={cardListColumnRef?.current?.children?.length! - 1}
            activeIndex={activeChildIndex}
            column
          />
        </Box>
      )}
    </Box>
  );
}
